import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Search,
  Filter,
  User,
  AlertCircle,
  RefreshCw,
  Copy,
  Check,
} from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"

type PaymentMethodType = "cashapp" | "venmo" | "zelle" | "paypal"
type RedemptionStatus = "pending" | "processing" | "completed" | "rejected"

interface Redemption {
  _id: string
  userId: string
  userEmail: string
  userDisplayName: string
  amount: number
  paymentMethod: PaymentMethodType
  paymentHandle: string
  status: RedemptionStatus
  rejectionReason?: string
  createdAt: string
  processedAt?: string
  processedByEmail?: string
}

const statusConfig: Record<RedemptionStatus, {
  icon: typeof Clock
  label: string
  bg: string
  border: string
  text: string
  iconColor: string
}> = {
  pending: {
    icon: Clock,
    label: "Pending",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    text: "text-amber-400",
    iconColor: "text-amber-400",
  },
  processing: {
    icon: Loader2,
    label: "Processing",
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    text: "text-blue-400",
    iconColor: "text-blue-400",
  },
  completed: {
    icon: CheckCircle2,
    label: "Completed",
    bg: "bg-green-500/10",
    border: "border-green-500/30",
    text: "text-green-400",
    iconColor: "text-green-400",
  },
  rejected: {
    icon: XCircle,
    label: "Rejected",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    text: "text-red-400",
    iconColor: "text-red-400",
  },
}

const paymentMethodLabels: Record<PaymentMethodType, string> = {
  cashapp: "Cash App",
  venmo: "Venmo",
  zelle: "Zelle",
  paypal: "PayPal",
}

const paymentMethodColors: Record<PaymentMethodType, string> = {
  cashapp: "bg-[#00D632]",
  venmo: "bg-[#008CFF]",
  zelle: "bg-[#6D1ED4]",
  paypal: "bg-[#003087]",
}

export function AdminRedemptions() {
  const { getAdminRedemptions, updateRedemptionStatus } = useAuth()
  const [redemptions, setRedemptions] = React.useState<Redemption[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [statusFilter, setStatusFilter] = React.useState<RedemptionStatus | "all">("all")
  const [searchQuery, setSearchQuery] = React.useState("")
  const [processingId, setProcessingId] = React.useState<string | null>(null)
  const [showRejectModal, setShowRejectModal] = React.useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = React.useState("")
  const [copiedHandle, setCopiedHandle] = React.useState<string | null>(null)

  const loadRedemptions = React.useCallback(async () => {
    setIsLoading(true)
    const filter = statusFilter === "all" ? undefined : statusFilter
    const result = await getAdminRedemptions(filter)
    if (result.success && result.redemptions) {
      setRedemptions(result.redemptions)
    }
    setIsLoading(false)
  }, [getAdminRedemptions, statusFilter])

  React.useEffect(() => {
    loadRedemptions()
  }, [loadRedemptions])

  const handleStatusUpdate = async (redemptionId: string, newStatus: RedemptionStatus, reason?: string) => {
    setProcessingId(redemptionId)
    const result = await updateRedemptionStatus(redemptionId, newStatus, reason)
    if (result.success) {
      await loadRedemptions()
    }
    setProcessingId(null)
    setShowRejectModal(null)
    setRejectionReason("")
  }

  const handleCopyHandle = async (handle: string) => {
    await navigator.clipboard.writeText(handle)
    setCopiedHandle(handle)
    setTimeout(() => setCopiedHandle(null), 2000)
  }

  const filteredRedemptions = React.useMemo(() => {
    if (!searchQuery) return redemptions
    const q = searchQuery.toLowerCase()
    return redemptions.filter(
      (r) =>
        r.userEmail.toLowerCase().includes(q) ||
        r.userDisplayName.toLowerCase().includes(q) ||
        r.paymentHandle.toLowerCase().includes(q)
    )
  }, [redemptions, searchQuery])

  const stats = React.useMemo(() => {
    const pending = redemptions.filter((r) => r.status === "pending").length
    const processing = redemptions.filter((r) => r.status === "processing").length
    const pendingAmount = redemptions
      .filter((r) => r.status === "pending" || r.status === "processing")
      .reduce((sum, r) => sum + r.amount, 0)
    return { pending, processing, pendingAmount }
  }, [redemptions])

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#111117] border border-white/[0.06] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.pending}</p>
              <p className="text-xs text-white/40">Pending Requests</p>
            </div>
          </div>
        </div>
        <div className="bg-[#111117] border border-white/[0.06] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.processing}</p>
              <p className="text-xs text-white/40">Processing</p>
            </div>
          </div>
        </div>
        <div className="bg-[#111117] border border-white/[0.06] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F37020]/10 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#F37020]" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">${stats.pendingAmount.toFixed(2)}</p>
              <p className="text-xs text-white/40">Pending Payout</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder="Search by email, name, or payment handle..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#111117] border border-white/[0.06] rounded-lg text-sm text-white placeholder-white/30 outline-none focus:border-[#F37020]/50"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-white/30" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as RedemptionStatus | "all")}
            className="px-3 py-2.5 bg-[#111117] border border-white/[0.06] rounded-lg text-sm text-white outline-none focus:border-[#F37020]/50"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
          <button
            onClick={loadRedemptions}
            disabled={isLoading}
            className="p-2.5 bg-[#111117] border border-white/[0.06] rounded-lg text-white/60 hover:text-white hover:border-white/20 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Redemptions Table */}
      <div className="bg-[#111117] border border-white/[0.06] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-white/30" />
          </div>
        ) : filteredRedemptions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <DollarSign className="w-12 h-12 text-white/10 mb-3" />
            <p className="text-white/40 text-sm">No redemptions found</p>
            {searchQuery && (
              <p className="text-white/20 text-xs mt-1">Try a different search term</p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Payment</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Date</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {filteredRedemptions.map((r) => {
                  const cfg = statusConfig[r.status]
                  const StatusIcon = cfg.icon
                  const date = new Date(r.createdAt)
                  const isProcessing = processingId === r._id

                  return (
                    <motion.tr
                      key={r._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-white/[0.06] flex items-center justify-center">
                            <User className="w-4 h-4 text-white/40" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">{r.userDisplayName}</p>
                            <p className="text-xs text-white/40">{r.userEmail}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-bold text-[#32CD32]">
                          ${r.amount.toFixed(2)}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded ${paymentMethodColors[r.paymentMethod]} flex items-center justify-center text-white text-xs font-bold`}>
                            {r.paymentMethod[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-xs text-white/60">{paymentMethodLabels[r.paymentMethod]}</p>
                            <button
                              onClick={() => handleCopyHandle(r.paymentHandle)}
                              className="flex items-center gap-1 text-xs text-white/40 hover:text-white transition-colors group"
                            >
                              <span className="font-mono">{r.paymentHandle}</span>
                              {copiedHandle === r.paymentHandle ? (
                                <Check className="w-3 h-3 text-green-400" />
                              ) : (
                                <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
                          <StatusIcon className={`w-3 h-3 ${r.status === "processing" ? "animate-spin" : ""}`} />
                          {cfg.label}
                        </span>
                        {r.status === "rejected" && r.rejectionReason && (
                          <p className="text-xs text-red-400/60 mt-1 max-w-[200px] truncate" title={r.rejectionReason}>
                            {r.rejectionReason}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-white/60">
                          {date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        </p>
                        <p className="text-xs text-white/30">
                          {date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {(r.status === "pending" || r.status === "processing") && (
                            <>
                              {r.status === "pending" && (
                                <button
                                  onClick={() => handleStatusUpdate(r._id, "processing")}
                                  disabled={isProcessing}
                                  className="px-2.5 py-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                                >
                                  {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Start"}
                                </button>
                              )}
                              <button
                                onClick={() => handleStatusUpdate(r._id, "completed")}
                                disabled={isProcessing}
                                className="px-2.5 py-1.5 text-xs font-medium text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg hover:bg-green-500/20 transition-colors disabled:opacity-50"
                              >
                                {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Complete"}
                              </button>
                              <button
                                onClick={() => setShowRejectModal(r._id)}
                                disabled={isProcessing}
                                className="px-2.5 py-1.5 text-xs font-medium text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {r.status === "completed" && r.processedByEmail && (
                            <span className="text-xs text-white/30">by {r.processedByEmail.split("@")[0]}</span>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowRejectModal(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#111117] border border-white/[0.08] rounded-xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Reject Redemption</h3>
                  <p className="text-sm text-white/40">This will restore the user's balance</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-white/60 mb-1.5">Reason (optional)</label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 outline-none focus:border-red-500/50 resize-none"
                    rows={3}
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowRejectModal(null)}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white/60 bg-white/[0.04] border border-white/[0.08] rounded-lg hover:bg-white/[0.08] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleStatusUpdate(showRejectModal, "rejected", rejectionReason)}
                    disabled={processingId === showRejectModal}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                  >
                    {processingId === showRejectModal ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Reject Redemption"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
